import { MessageSquare, Mic, X } from 'lucide-react';
import { BaseSheet } from '@/components/common/BaseSheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import VoiceAgentAvatar from '@/components/VoiceAgent/VoiceAgentAvatar';
import { useQuickTestConversation } from '@/hooks/voice/useQuickTestConversation';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import { GenieTextChatOverlayWeb } from '@/pages/agent-interfaces/live-agent/components/GenieTextChatOverlayWeb';
import type { GenieQuickTestModalProps } from '@/types/agent/quick-test';
import { DEFAULT_BRANDING } from '@/types/ui/branding';

const QuickTestContent = ({ genie, onClose }: Omit<GenieQuickTestModalProps, 'isOpen'>) => {
  const { mode, messages, voice, audio, chat, isBusy, changeMode, close, endChat } = useQuickTestConversation(genie, onClose);
  const isMobile = useIsMobile(640);
  const brand = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  return (
    <BaseSheet
      isOpen
      onClose={close}
      title={`${genie.name} — Quick Test`}
      description="Test your Genie with chat or voice."
      hideTitle
      size="xl"
      side={isMobile ? 'bottom' : 'right'}
      contentClassName="h-[92dvh] rounded-t-2xl p-0 sm:h-[100dvh] sm:rounded-none"
      className="flex min-h-0 flex-col overflow-hidden py-0"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-genie-border p-6">
        <div className="min-w-0">
          <h2 className="truncate text-xl font-bold text-gray-900">{genie.name}</h2>
          <p className="text-sm text-gray-500">Quick Test · Chat &amp; Voice</p>
        </div>
        <Button variant="ghost" size="icon" onClick={close} disabled={isBusy} aria-label="Close Quick Test" className="min-h-[44px] min-w-[44px] rounded-full">
          <X className="h-5 w-5" />
        </Button>
      </div>
      <Tabs value={mode} onValueChange={changeMode} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 px-6 pt-5 pb-3">
          <div className="rounded-xl bg-gradient-to-r from-genie-primary to-genie-secondary p-[2px]">
            <TabsList aria-label="Quick Test channel" className="grid h-auto w-full grid-cols-2 gap-1 rounded-[10px] bg-white p-1">
              <TabsTrigger value="chat" disabled={isBusy} className="min-h-[44px] gap-2 rounded-lg data-[state=active]:bg-genie-primary/10 data-[state=active]:text-genie-primary">
                <MessageSquare className="h-4 w-4" /> Chat
              </TabsTrigger>
              <TabsTrigger value="voice" disabled={isBusy} className="min-h-[44px] gap-2 rounded-lg data-[state=active]:bg-genie-primary/10 data-[state=active]:text-genie-primary">
                <Mic className="h-4 w-4" /> Voice
              </TabsTrigger>
            </TabsList>
          </div>
          <p className="mt-3 text-center text-xs text-gray-500">
            Switching channels ends the current test conversation.
          </p>
        </div>
        <TabsContent value="chat" className="mt-0 min-h-0 flex-1 overflow-hidden px-3 pb-[env(safe-area-inset-bottom)]">
          <GenieTextChatOverlayWeb
            inDrawer
            hideClose
            brandColor={brand}
            secondaryColor={genie.branding?.secondaryColor}
            agentName={genie.name}
            messages={messages}
            inlineMessage={chat.inlineMessage}
            onChangeInlineMessage={chat.setInlineMessage}
            onSend={() => { if (!isBusy) void chat.sendInlineMessage(); }}
            isConnecting={isBusy}
            isSpeaking={voice.speaking}
            isTextOnlyMode={chat.isTextOnlyMode}
            isAllAudioMuted={audio.isAllAudioMuted}
            onToggleAllAudioMute={audio.toggleAllAudioMute}
            startedFromText={chat.startedFromText}
            onCloseOverlay={close}
            onEndChat={endChat}
            chatStyle={{ subtitleText: 'Type a message to test your Genie. No microphone needed.' }}
          />
        </TabsContent>
        <TabsContent value="voice" className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <div className="flex min-h-full flex-col items-center justify-center gap-6">
            <VoiceAgentAvatar
              brandColor={brand}
              size={isMobile ? 260 : 320}
              customBranding={genie.branding}
              onClick={() => { if (!isBusy) void voice.toggleConversation(); }}
              speaking={voice.speaking}
              listening={voice.listening}
              isConnecting={voice.isConnecting}
              connected={voice.connected}
            />
            <div className="text-center" role="status">
              <p className="text-sm font-medium text-gray-600">{voice.getStatusMessage()}</p>
              <p className="mt-1 text-xs text-gray-500">{voice.connected ? 'Voice conversation in progress' : 'Click the avatar or start button to begin'}</p>
            </div>
            <Button
              onClick={() => void voice.toggleConversation()}
              disabled={isBusy}
              className="min-h-[44px] rounded-xl bg-gradient-to-r from-genie-primary to-genie-secondary px-6 font-semibold text-white shadow-lg shadow-genie-primary/30 hover:opacity-90"
            >
              {voice.isConnecting ? 'Connecting...' : voice.connected ? 'End Voice Conversation' : 'Start Voice Conversation'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </BaseSheet>
  );
};

export const GenieQuickTestModal = ({ genie, isOpen, onClose }: GenieQuickTestModalProps) => (
  isOpen ? <QuickTestContent key={genie.id} genie={genie} onClose={onClose} /> : null
);
